import { useEffect, useState } from 'react';
import {
  ExpenseDataFragment,
  useExpenseByIdLazyQuery,
} from '../../../../__generated__/graphql';

export const OCR_POLLING_INTERVAL_MS = 2000;
export const OCR_MAX_POLLING_TIME_MS = 30000;

type UseOcrPollingParams = {
  expenseData: ExpenseDataFragment | null;
  expenseId: string | null;
};

type OcrState =
  | { status: 'polling' | 'timeout' | null; completedAt?: undefined }
  | { status: 'completed'; completedAt: string };

export const useOcrPolling = ({
  expenseData,
  expenseId,
}: UseOcrPollingParams) => {
  const [refetchExpenseById] = useExpenseByIdLazyQuery({
    fetchPolicy: 'network-only',
  });

  const getInitialState = (): OcrState => {
    if (!expenseData) return { status: null };
    const isOcrActive = expenseData.flair__OCR__c === true;
    const completedAt = expenseData.flair__OCR_Completed_At__c ?? null;

    if (!isOcrActive) return { status: null };
    if (completedAt) return { status: 'completed', completedAt };
    return { status: 'polling' };
  };

  const [ocrState, setOcrState] = useState<OcrState>(() => getInitialState());

  useEffect(() => {
    if (!expenseData || !expenseId) {
      setOcrState({ status: null });
      return;
    }

    const isOcrActive = expenseData.flair__OCR__c === true;
    const completedAt = expenseData.flair__OCR_Completed_At__c ?? null;

    if (isOcrActive) {
      if (completedAt) {
        setOcrState({ status: 'completed', completedAt });
        return;
      }
      setOcrState({ status: 'polling' });
    } else {
      setOcrState({ status: null });
      return;
    }

    let cancelled = false;
    let intervalId: NodeJS.Timeout | undefined;
    const startedAt = Date.now();

    const pollOcr = async () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= OCR_MAX_POLLING_TIME_MS) {
        if (!cancelled) setOcrState({ status: 'timeout' });
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
        return;
      }

      try {
        const { data } = await refetchExpenseById({
          variables: { id: expenseId },
        });
        const expense = data?.expense;

        const isOcrActive = expense?.flair__OCR__c === true;
        const completedAt = expense?.flair__OCR_Completed_At__c ?? null;

        if (!isOcrActive) {
          if (!cancelled) setOcrState({ status: null });
          cancelled = true;
          if (intervalId) clearInterval(intervalId);
          return;
        }
        if (completedAt) {
          if (!cancelled) setOcrState({ status: 'completed', completedAt });
          cancelled = true;
          if (intervalId) clearInterval(intervalId);
          return;
        }
        if (!cancelled) setOcrState({ status: 'polling' });
      } catch (error) {
        if (!cancelled) setOcrState({ status: null });
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
      }
    };

    pollOcr();
    intervalId = setInterval(pollOcr, OCR_POLLING_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [expenseData, expenseId, refetchExpenseById]);

  return {
    ocrStatus: ocrState.status,
    ocrCompletedAt:
      ocrState.status === 'completed' ? ocrState.completedAt : undefined,
  };
};
